import { Button } from '@/components/ui/Button'
import { AdminPollCard } from '../pepper-inc/cards/AdminPollCard'
import { CreatePollDialog } from '../pepper-inc/dialogs/create-edit-poll/CreatePollDialog'
import { EditPollDialog } from '../pepper-inc/dialogs/create-edit-poll/EditPollDialog'
import { TopSection } from './sections/TopSection'
import { AdminStatsSection } from './sections/admin-stats/AdminStatsSection'
import { getPollOptions } from '@/utils/polls/getPollsOptions'
import { Skeleton } from '@/components/ui/Skeleton'
import { useEffect, useMemo, useState } from 'react'
import { waitForNextMinedBlocks } from '@/utils/wagmi/wait-for-block'
import { toast } from 'react-toastify'
import { surveyQueriesFactory, useSurveys } from '@/data-access/surveys/queries'
import { useInView } from 'react-intersection-observer'
import { Spinner } from '@/components/ui/Spinner'
import { getSurveysFromChain } from '@/data-access/surveys/api'
import { useQueryClient, type InfiniteData } from '@tanstack/react-query'
import type { GetSurveyResponse, GetSurveysParams, Survey } from '@/data-access/surveys/types'
import type { Hex } from 'viem'
import { useChainId } from 'wagmi'
import { useResponsiveDialog } from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'

const ONGOING_SURVEYS_PARAMS = {
	page: 1,
	perPage: 10,
	state: ['ongoing'],
	orderBy: 'endDate',
	direction: 'asc'
} satisfies GetSurveysParams

const UPCOMING_SURVEYS_PARAMS = {
	page: 1,
	perPage: 10,
	state: ['upcoming'],
	orderBy: 'startDate',
	direction: 'asc'
} satisfies GetSurveysParams

const PepperIncAdminPage = () => {
	const queryClient = useQueryClient()
	const chainId = useChainId()

	const { data: ongoingData, isPending: isPendingOngoing } = useSurveys(ONGOING_SURVEYS_PARAMS)
	const { data: upcomingData, isPending: isPendingUpcoming } = useSurveys(UPCOMING_SURVEYS_PARAMS)

	const {
		data: endedData,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
		isPending: isPendingEnded
	} = useSurveys({
		page: 1,
		perPage: 3,
		state: ['ended'],
		orderBy: 'endDate',
		direction: 'desc'
	})

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0.15,
		triggerOnce: false
	})

	const flatOngoingSurveys = useMemo(() => ongoingData?.pages.flatMap(page => page.surveys), [ongoingData])
	const flatUpcomingSurveys = useMemo(() => upcomingData?.pages.flatMap(page => page.surveys), [upcomingData])
	const flatEndedSurveys = useMemo(() => endedData?.pages.flatMap(page => page.surveys), [endedData])

	const [createPollDialogProps, openCreatePollDialog, closeCreatePollDialog] = useResponsiveDialog({
		resetOnClose: true
	})
	const [editPollDialogProps, openEditPollDialog, closeEditPollDialog] = useResponsiveDialog({ resetOnClose: true })
	const [pollToEdit, setPollToEdit] = useState<Survey | undefined>()

	const onEditPressed = (address: Hex) => {
		const chosenPoll = [...(flatOngoingSurveys ?? []), ...(flatUpcomingSurveys ?? [])].find(
			poll => poll.surveyContract === address
		)

		setPollToEdit(chosenPoll)
		openEditPollDialog()
	}

	const updateSurvey = async (cb: () => Promise<void>) => {
		await toast.promise(waitForNextMinedBlocks(2).then(cb), {
			pending: 'Refreshing polls',
			success: 'List updated',
			error: 'Failed to update list'
		})

		toast.warn(
			'After refreshing the page, you might still see the old data until the indexer catches up with the chain'
		)
	}

	const onSubmitCreateDialog = async (surveyAddress: Hex) => {
		closeCreatePollDialog()
		updateSurvey(async () => {
			const [survey] = await getSurveysFromChain([surveyAddress])

			queryClient.setQueryData(
				[chainId, ...surveyQueriesFactory.paginated(UPCOMING_SURVEYS_PARAMS)],
				(oldData: InfiniteData<GetSurveyResponse>) => {
					return {
						...oldData,
						pages: [
							{
								...oldData?.pages[0],
								surveys: [survey, ...(oldData?.pages[0].surveys ?? [])]
							}
						]
					}
				}
			)
		})
	}

	const onSubmitEditDialog = async (surveyAddress: Hex) => {
		closeEditPollDialog()
		updateSurvey(async () => {
			const [survey] = await getSurveysFromChain([surveyAddress])

			queryClient.setQueryData(
				[
					chainId,
					...surveyQueriesFactory.paginated(
						survey.status === 'ongoing' ? ONGOING_SURVEYS_PARAMS : UPCOMING_SURVEYS_PARAMS
					)
				],
				(oldData: InfiniteData<GetSurveyResponse>) => {
					const editedSurveyIdx = oldData.pages[0].surveys.findIndex(s => s.surveyContract === surveyAddress)
					const updatedSurveys = [...oldData.pages[0].surveys]
					updatedSurveys[editedSurveyIdx] = survey

					return {
						...oldData,
						pages: [
							{
								...oldData.pages[0],
								surveys: updatedSurveys
							}
						]
					}
				}
			)
		})
	}

	useEffect(() => {
		if (inView) {
			fetchNextPage()
		}
	}, [inView])

	return (
		<div className="mx-auto bg-green-10">
			<TopSection />
			<AdminStatsSection />
			<div className="flex justify-center px-4 py-10">
				<div className="w-full max-w-4xl flex flex-col gap-10">
					<Button
						variant="danger"
						className="py-5 px-10 w-full md:w-fit leading-none"
						loading={false}
						onClick={openCreatePollDialog}
						data-testid={testIdFactory.adminPollCreateModalButton()}
					>
						Create New Poll
					</Button>
					{/* Ongoing, Upcoming, Ended Polls */}
					<PollSection
						polls={flatOngoingSurveys}
						isLoading={isPendingOngoing}
						variant="ongoing"
						onPressEdit={onEditPressed}
					/>
					<PollSection
						polls={flatUpcomingSurveys}
						isLoading={isPendingUpcoming}
						variant="upcoming"
						onPressEdit={onEditPressed}
						onPressDelete={() => console.log('pressed del')}
					/>
					<PollSection
						polls={flatEndedSurveys}
						isLoading={isPendingEnded}
						variant="ended"
						onPressDuplicate={() => console.log('Pressed duplicate')}
					/>
				</div>
			</div>
			<div className="flex flex-col items-center w-full">
				{isFetchingNextPage && <Spinner size={24} />}
				{hasNextPage && <div ref={loadMoreRef} className="w-full h-10" />}
			</div>
			<CreatePollDialog
				onSubmit={onSubmitCreateDialog}
				{...createPollDialogProps}
				key={createPollDialogProps.key}
			/>
			{pollToEdit && (
				<EditPollDialog
					poll={pollToEdit}
					onSubmit={onSubmitEditDialog}
					{...editPollDialogProps}
					key={editPollDialogProps.key}
				/>
			)}
		</div>
	)
}

type DiscriminatedUnion =
	| {
			onPressEdit?: (address: Hex) => void
			onPressDelete?: never
			onPressDuplicate?: never
			variant: 'ongoing' | 'upcoming'
	  }
	| {
			onPressDelete?: (address: string) => void
			onPressEdit?: (address: Hex) => void
			onPressDuplicate?: never
			variant: 'upcoming'
	  }
	| {
			onPressDuplicate?: (address: Hex) => void
			onPressEdit?: never
			onPressDelete?: never
			variant: 'ended'
	  }

type PollSectionProps = {
	polls: Survey[] | undefined
	isLoading: boolean
} & DiscriminatedUnion

function PollSection({ polls, isLoading, variant, onPressDelete, onPressDuplicate, onPressEdit }: PollSectionProps) {
	return (
		<div className="flex flex-col gap-5">
			<p className="text-6xl font-pp-neue-bit font-bold leading-[42.85px] uppercase">{`${variant} Polls`}</p>
			{isLoading ? (
				<Skeleton className="w-full h-48" />
			) : (
				<>
					{!polls?.length ? (
						<div>{`No ${variant} polls found`}</div>
					) : (
						polls?.map((poll, idx) => (
							<AdminPollCard
								address={poll.surveyContract}
								key={idx}
								testIdDiscriminator={`Admin${idx}`}
								question={poll.question}
								startDate={poll.startDate}
								endDate={poll.endDate}
								options={getPollOptions(poll.answers)}
								onPressDelete={onPressDelete}
								onPressEdit={onPressEdit}
								onPressDuplicate={onPressDuplicate}
							/>
						))
					)}
				</>
			)}
		</div>
	)
}

export default PepperIncAdminPage
